import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    TextField,
    Typography,
} from '@mui/material';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { ValidatePromoService } from '../../../services/billing.service';

/**
 * PromoCodeInput
 *
 * Props:
 *   billingMonth  {number}   – YYYYMM
 *   invoiceAmount {number}   – pre-discount total in USD dollars
 *   onChange      {function} – (PromoState) => void  called on every state change
 */
const PromoCodeInput = ({ billingMonth, invoiceAmount, onChange }) => {
    const [code, setCode]       = useState('');
    const [status, setStatus]   = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
    const [message, setMessage] = useState('');
    const [promo, setPromo]     = useState(null);   // PromoSnapshot | null

    // Track the previous invoiceAmount so the reset effect only fires on a
    // genuine change, not on every render (which would cause infinite loops).
    const prevAmountRef = useRef(invoiceAmount);

    /* ── helpers ──────────────────────────────────────────────────────── */

    const fmt = (n) => Number(n || 0).toFixed(2);

    const buildState = (snapshot, amount) => ({
        promoCode:      snapshot ? snapshot.promo_code      : null,
        discountAmount: snapshot ? snapshot.discount_amount : 0,
        finalAmount:    snapshot ? snapshot.final_amount    : amount,
        promoSnapshot:  snapshot,
    });

    /* ── reset when invoiceAmount genuinely changes (user revisits) ────  */
    useEffect(() => {
        // Skip the very first mount and cases where the value hasn't changed.
        if (prevAmountRef.current === invoiceAmount) return;
        prevAmountRef.current = invoiceAmount;

        setCode('');
        setStatus('idle');
        setMessage('');
        setPromo(null);
        // Only notify parent if there was an active promo to clear.
        // If there was none, the parent's promoState is already correct and we
        // must NOT call onChange here — doing so on every render is what caused
        // the infinite loop.
        onChange(buildState(null, invoiceAmount));
        // onChange is intentionally excluded from deps; it should be a stable
        // setter (useState dispatch) in the parent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceAmount]);

    /* ── apply promo ────────────────────────────────────────────────── */
    const handleApply = async () => {
        const trimmed = code.trim();
        if (!trimmed) return;

        setStatus('loading');
        setMessage('');

        try {
            const response = await ValidatePromoService({
                promo_code:     trimmed,
                invoice_amount: invoiceAmount,
                billing_month:  billingMonth,
            });

            const snapshot = response?.data;
            if (!snapshot) {
                setStatus('error');
                setMessage('Could not validate promo code. Please try again.');
                setPromo(null);
                onChange(buildState(null, invoiceAmount));
                return;
            }

            setPromo(snapshot);
            setStatus('success');
            setMessage('');
            onChange(buildState(snapshot, invoiceAmount));
        } catch (err) {
            const errMsg =
                err?.message && err.message !== 'NETWORK ERROR : Some Thing Went Wrong!'
                    ? err.message
                    : 'Could not validate promo code. Please try again.';
            setStatus('error');
            setMessage(errMsg);
            setPromo(null);
            onChange(buildState(null, invoiceAmount));
        }
    };

    /* ── remove promo ───────────────────────────────────────────────── */
    const handleRemove = () => {
        setCode('');
        setStatus('idle');
        setMessage('');
        setPromo(null);
        onChange(buildState(null, invoiceAmount));
    };

    /* ── discount badge label ───────────────────────────────────────── */
    const discountBadge = promo
        ? promo.promo_type === 'percentage'
            ? `${promo.discount_value}% OFF`
            : `$${promo.discount_value} OFF`
        : null;

    /* ── render ─────────────────────────────────────────────────────── */
    return (
        <Box sx={{ mt: 2 }}>
            {/* ── Input row ── */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    size="small"
                    label="Promo Code"
                    placeholder="e.g. PCT15"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    disabled={status === 'success' || status === 'loading'}
                    error={status === 'error'}
                    inputProps={{ style: { fontFamily: 'monospace', letterSpacing: 1 } }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: status === 'success' ? '#4caf50' : undefined },
                            '&:hover fieldset': { borderColor: status === 'success' ? '#4caf50' : '#ff6468' },
                            '&.Mui-focused fieldset': { borderColor: status === 'success' ? '#4caf50' : '#ff6468' },
                        },
                    }}
                />

                {/* Apply button (hidden after success) */}
                {status !== 'success' && (
                    <Button
                        variant="outlined"
                        onClick={handleApply}
                        disabled={status === 'loading' || code.trim() === ''}
                        startIcon={
                            status === 'loading'
                                ? <CircularProgress size={14} thickness={5} />
                                : <LocalOfferIcon style={{ fontSize: 16 }} />
                        }
                        sx={{
                            borderColor: '#ff6468',
                            color: '#ff6468',
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            '&:hover': { borderColor: '#e55458', backgroundColor: 'rgba(255,100,104,0.05)' },
                            '&.Mui-disabled': { borderColor: '#e0e0e0', color: '#bdbdbd' },
                        }}
                    >
                        {status === 'loading' ? 'Validating…' : 'Apply'}
                    </Button>
                )}

                {/* Remove button (shown after success) */}
                {status === 'success' && (
                    <Button
                        variant="outlined"
                        onClick={handleRemove}
                        sx={{
                            borderColor: '#9e9e9e',
                            color: '#757575',
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            '&:hover': { borderColor: '#616161', backgroundColor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        Remove
                    </Button>
                )}
            </Box>

            {/* ── Error message ── */}
            {status === 'error' && message && (
                <Typography
                    variant="caption"
                    sx={{ color: '#d32f2f', mt: 0.5, display: 'block', pl: 0.5 }}
                >
                    ✗ {message}
                </Typography>
            )}

            {/* ── Discount summary (only when status === 'success') ── */}
            {status === 'success' && promo && (
                <Box
                    sx={{
                        mt: 1.5,
                        border: '1px solid #c8e6c9',
                        borderRadius: '8px',
                        backgroundColor: '#f1f8e9',
                        overflow: 'hidden',
                    }}
                >
                    {/* Description */}
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#388e3c', fontStyle: 'italic' }}>
                            ✓ {promo.description}
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: '#c8e6c9' }} />

                    {/* Original amount row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">
                            Original Amount
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            ${fmt(promo.original_amount)}
                        </Typography>
                    </Box>

                    {/* Discount row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                                Discount
                            </Typography>
                            {discountBadge && (
                                <Chip
                                    label={discountBadge}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#e8f5e9',
                                        color: '#2e7d32',
                                        border: '1px solid #a5d6a7',
                                        fontWeight: 'bold',
                                        fontSize: '10px',
                                        height: '20px',
                                    }}
                                />
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                            -${fmt(promo.discount_amount)}
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: '#c8e6c9', mx: 2 }} />

                    {/* Total Due row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1b5e20' }}>
                            Total Due
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1b5e20', fontSize: '1.1rem' }}>
                            ${fmt(promo.final_amount)}
                        </Typography>
                    </Box>

                    {/* Recurring note */}
                    {promo.recurring && (
                        <Box sx={{ px: 2, pb: 1.25 }}>
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#388e3c' }}>
                                This discount will be applied for{' '}
                                {promo.recurring_duration_months != null
                                    ? promo.recurring_duration_months
                                    : '∞'}{' '}
                                billing cycle(s).
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default PromoCodeInput;

